import React from "react";

import { Label } from "src/base-components/Label";
import { PillToggle } from "src/base-components/PillToggle";
import { Textarea } from "src/base-components/Textarea";
import { Callout } from "src/design-system/Callout";
import { ErrorHint } from "src/design-system/ErrorHint";
import { KeySupplyMode } from "src/oauthClients/resolveKeyMaterial";

const PUBLIC_KEY_PLACEHOLDER =
  "Paste your public key as a JWK or as an SPKI PEM\n-----BEGIN PUBLIC KEY-----";

type Props = {
  mode: KeySupplyMode;
  pastedKey: string;
  error?: string;
  onModeChange: (mode: KeySupplyMode) => void;
  onPastedKeyChange: (value: string) => void;
};

export const PublicKeyFieldset: React.FC<Props> = ({
  mode,
  pastedKey,
  error,
  onModeChange,
  onPastedKeyChange,
}) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-col gap-2">
      <Label mb="mb-0">Signing key</Label>
      <PillToggle value={mode} onChange={onModeChange}>
        <PillToggle.Button value="generate">Generate for me</PillToggle.Button>
        <PillToggle.Button value="provide">Use my own key</PillToggle.Button>
      </PillToggle>
    </div>

    {mode === "generate" ? (
      <Callout dataLoc="oauth-client-generate-hint" variant="info">
        We generate an RSA-2048 key pair in your browser. Taktile only ever
        receives the public key; the private key is offered to you as a one-time
        download and is never sent anywhere.
      </Callout>
    ) : (
      <div className="flex flex-col gap-2">
        <Textarea
          aria-label="Public key"
          data-loc="oauth-client-public-key-input"
          errored={Boolean(error)}
          placeholder={PUBLIC_KEY_PLACEHOLDER}
          rows={6}
          value={pastedKey}
          fullWidth
          onChange={(event) => onPastedKeyChange(event.target.value)}
        />
        <Callout dataLoc="oauth-client-provide-hint" variant="info">
          The key is converted to a JWK in your browser. Only the public key
          reaches Taktile, so keep the matching private key on your side.
        </Callout>
      </div>
    )}

    {Boolean(error) && <ErrorHint>{error}</ErrorHint>}
  </div>
);
